import {
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import SelectMenu from '../../../forms/SelectMenu';
import { useTranslation } from 'react-i18next';
import {
  SendMessageActionFormData,
  SendMessageRecipient,
  SendMessageType,
} from '../../../../data/models/simplified-workflow.model';
import {
  ControllerRenderProps,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import TextInput from '../../../forms/TextInput';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Search } from '@mui/icons-material';
import TextSearch from '../../../controls/TextSearch';
import { hasWordStartingWith } from '../../../../utils/string.utils';
import AddIcon from '@mui/icons-material/Add';
import * as lookupSlice from '../../../../data/lookup.slice';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { SmartLabel } from '../../../../data/models/smart-label.model';
import simplifiedWorkflowUtils from '../../../../services/simplifiedWorkflowUtils';
import Checkbox from '../../../forms/Checkbox';
import workflowChartUtils from '../../../../services/workflowChartUtils';
import workflowUtils from '../../../../services/workflowUtils';

const PREFIX = 'SendMessageActionStepForm';

const classes = {
  container: `${PREFIX}-container`,
  menu: `${PREFIX}-menu`,
  search: `${PREFIX}-search`,
  menuItem: `${PREFIX}-menuItem`,
  messageInput: `${PREFIX}-messageInput`,
  smartLabelMenuBar: `${PREFIX}-smartLabelMenuBar`,
  smartLabelMenuBarError: `${PREFIX}-smartLabelMenuBarError`,
  smartLabelMenuButtonContainer: `${PREFIX}-smartLabelMenuButtonContainer`,
  divider: `${PREFIX}-divider`,
  bottomContentContainer: `${PREFIX}-bottomContentContainer`,
  stoCheckbox: `${PREFIX}-stoCheckbox`,
  sameAsCheckbox: `${PREFIX}-sameAsCheckbox`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    gap: theme.spacing(3),
  },
  [`& .${classes.menuItem}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    whiteSpace: 'normal',
  },
  [`& .${classes.messageInput}`]: {
    ['textarea']: {
      paddingBottom: '30px !important',
    },
  },
  [`& .${classes.smartLabelMenuBar}`]: {
    position: 'relative',
    bottom: 65,
  },
  [`& .${classes.smartLabelMenuBarError}`]: {
    position: 'relative',
    bottom: 90,
  },
  [`& .${classes.smartLabelMenuButtonContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
  },
  [`& .${classes.divider}`]: {
    marginLeft: 10,
    marginRight: 10,
  },
  [`& .${classes.bottomContentContainer}`]: {
    marginTop: -60,
    gap: theme.spacing(3),
  },
  [`& .${classes.stoCheckbox}`]: {
    height: 24,
  },
  [`& .${classes.sameAsCheckbox}`]: {
    marginTop: -80,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  [`& .${classes.menu}`]: {
    width: 400,
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.search}`]: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    marginBottom: 10,
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  [`& .${classes.menuItem}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    whiteSpace: 'normal',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

interface SendMessageActionFormProps {
  handleFormUpdate: () => void;
}

const smsMaxChars = 480;
const emailMaxChars = 1024;

export default function SendMessageActionForm({
  handleFormUpdate,
}: SendMessageActionFormProps) {
  const { t } = useTranslation('common');
  const { control, formState, setValue, getValues } =
    useFormContext<SendMessageActionFormData>();
  const [smartLabelSearchText, setSmartLabelSearchText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [smartLabelTextInputName, setSmartLabelTextInputName] =
    useState<keyof SendMessageActionFormData>('message');
  const smartLabels = useAppSelector(lookupSlice.selectCommsCenterSmartLabels);
  const messageTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const reminderMessageTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const open = Boolean(anchorEl);

  const filteredSmartLabels = smartLabels.filter((smartLabel) =>
    hasWordStartingWith(smartLabel.smartLabelName, smartLabelSearchText),
  );

  const yesOrNoOptions = [
    {
      key: t('common.yes'),
      value: true,
    },
    {
      key: t('common.no'),
      value: false,
    },
  ];

  const selectableMessageTypes = Object.values(SendMessageType).map(
    (value) => ({
      key: t(
        `workflows.${simplifiedWorkflowUtils.getTranslationKeyForSendMessageType(value)}`,
      ),
      value,
    }),
  );

  const selectableReceipients = Object.values(SendMessageRecipient).map(
    (value) => ({
      key: t(
        `workflows.${simplifiedWorkflowUtils.getTranslationKeyForSendMessageRecipient(value)}`,
      ),
      value,
    }),
  );

  const messageType = useWatch({ control, name: 'messageType' });
  const maxMessageLength =
    messageType === SendMessageType.SMS ? smsMaxChars : emailMaxChars;
  const recipient = useWatch({ control, name: 'recipient' });
  const message = useWatch({ control, name: 'message' });
  const reminderMessage = useWatch({ control, name: 'alertReminder' });
  const isInteractive = useWatch({ control, name: 'interactiveResponse' });
  const sendReminder = useWatch({ control, name: 'sendReminderIfNoResponse' });
  const sameAsInitialMessage = useWatch({
    control,
    name: 'sameAsInitialMessage',
  });
  const branches = useWatch({ control, name: 'branches' });

  useEffect(() => {
    handleFormUpdate();
  }, [messageType, recipient, isInteractive, branches]);

  useEffect(() => {
    if (sameAsInitialMessage) {
      setValue('alertReminder', message);
    }
  }, [sameAsInitialMessage, message]);

  useEffect(() => {
    if (isNil(isInteractive)) {
      return;
    }
    if (isInteractive) {
      setValue('branches', workflowUtils.getConditionalNames());
    } else {
      setValue('branches', []);
    }
  }, [isInteractive]);

  const handleShowSmartLabelButtonClick = (
    event: any,
    inputName: keyof SendMessageActionFormData,
  ) => {
    setAnchorEl(event.currentTarget);
    setSmartLabelTextInputName(inputName);
  };

  const getTextAreaRef = () => {
    return smartLabelTextInputName === 'message'
      ? messageTextAreaRef
      : reminderMessageTextAreaRef;
  };

  const getMessageField = () => {
    return smartLabelTextInputName === 'message' ? message : reminderMessage;
  };

  const handleSmartLabelClick = (smartLabel: SmartLabel) => {
    const message = getMessageField();
    const bracketPairs = findBracketPairs(message ?? '');
    for (const [bracketStart, bracketEnd] of bracketPairs) {
      // Do not allow embedding a smart label into another smart label
      if (cursorPosition >= bracketStart && cursorPosition <= bracketEnd) {
        return;
      }
    }
    const smartLabelText = `[${smartLabel.smartLabelName}]`;
    const newMessage = `${message?.substring(
      0,
      cursorPosition,
    )}${smartLabelText}${message?.substring(cursorPosition)}`;

    // Update the form field value
    setValue(smartLabelTextInputName, newMessage);

    // Calculate the new cursor position after the inserted word
    const newPosition = cursorPosition + smartLabelText.length;

    // Set focus back to the textarea and position cursor
    const textArea = getTextAreaRef().current;
    if (textArea) {
      setTimeout(() => {
        textArea.focus();
        textArea.setSelectionRange(newPosition, newPosition);
        setCursorPosition(newPosition);
      }, 0);
    }
  };

  const handleSearchTextChange = (val: string) => {
    setSmartLabelSearchText(val);
  };

  const handleMessageInputClick = (
    e: React.MouseEvent<HTMLDivElement>,
    inputName: keyof SendMessageActionFormData,
  ) => {
    // Save the current cursor position when the text field is clicked or edited
    const target = e.target as HTMLTextAreaElement;
    setCursorPosition(target.selectionStart || 0);
    setSmartLabelTextInputName(inputName);
  };

  const handleMessageInputKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Update cursor position on keyup events
    const target = e.target as HTMLTextAreaElement;
    setCursorPosition(target.selectionStart || 0);
  };

  const handleMessageTextInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: ControllerRenderProps,
  ) => {
    const message = getMessageField();
    const newValue = e.target.value;
    const oldValue = message;
    if (!oldValue) {
      return;
    }
    // If the user delete characters in a smart label then delete
    // the entire smart label
    if (oldValue.length > newValue.length) {
      handleSmartLabelRemovals(oldValue, newValue);
    }
    // If the user adds characters into a smart label then
    // remove the added characters to prevent modification of the
    // smart label
    else if (oldValue.length < newValue.length) {
      handleSmartLabelAdditions(oldValue, newValue);
    }
  };

  // Handle removal of characters between smart label brackets []
  // If characters between smart label brackets are removed then delete
  // the smart label
  const handleSmartLabelRemovals = (oldValue: string, newValue: string) => {
    const oldBracketPairs = findBracketPairs(oldValue);
    // If no bracket pairs, return early
    if (!oldBracketPairs.length) return;

    // Find the deletion boundaries
    const differences = findDifferences(oldValue, newValue);

    if (differences) {
      const [startIndex, endIndex] = differences;

      // Check if the deleted section overlaps with any bracket pair
      for (const [bracketStart, bracketEnd] of oldBracketPairs) {
        // Check if deletion overlaps with brackets or content inside brackets
        const deletionOverlapsBrackets =
          // Starting bracket is deleted
          (startIndex <= bracketStart && endIndex >= bracketStart) ||
          // Ending bracket is deleted
          (startIndex <= bracketEnd && endIndex >= bracketEnd) ||
          // Any character inside brackets is deleted
          (startIndex >= bracketStart && startIndex <= bracketEnd);

        if (deletionOverlapsBrackets) {
          // Create a new value by removing the entire bracket content
          const beforeBracket = oldValue.substring(0, bracketStart);
          const afterBracket = oldValue.substring(bracketEnd + 1);
          const finalValue = beforeBracket + afterBracket;

          // Update the form value
          setTimeout(() => {
            setValue(smartLabelTextInputName, finalValue);
            const cursorPosition = beforeBracket.length;
            setTimeout(() => {
              getTextAreaRef().current?.setSelectionRange(
                cursorPosition,
                cursorPosition,
              );
              setCursorPosition(cursorPosition);
            }, 0);
          }, 0);
          break;
        }
      }
    }
  };

  // Handle addition of characters between smart label brackets []
  // If characters are added between smart labels then delete the added characters
  const handleSmartLabelAdditions = (oldValue: string, newValue: string) => {
    const oldBracketPairs = findBracketPairs(oldValue);
    const newBracketPairs = findBracketPairs(newValue);

    // If no bracket pairs in either, return early
    if (!oldBracketPairs.length && !newBracketPairs.length) return;

    // Find the insertion boundaries
    const differences = findAdditions(oldValue, newValue);
    if (differences) {
      const [insertPosition, insertedText] = differences;

      // Check if the insertion is inside any bracket pair from the old value
      for (const [bracketStart, bracketEnd] of oldBracketPairs) {
        // Check if insertion is inside brackets
        const insertionInsideBrackets =
          insertPosition > bracketStart && insertPosition <= bracketEnd;

        if (insertionInsideBrackets) {
          // Delete only the newly added characters by reconstructing the text
          // without the inserted characters
          const beforeInsertion = newValue.substring(0, insertPosition);
          const afterInsertion = newValue.substring(
            insertPosition + insertedText.length,
          );
          const finalValue = beforeInsertion + afterInsertion;

          setTimeout(() => {
            setValue(smartLabelTextInputName, finalValue);
            const cursorPosition = beforeInsertion.length;
            setTimeout(() => {
              getTextAreaRef().current?.setSelectionRange(
                cursorPosition,
                cursorPosition,
              );
              setCursorPosition(cursorPosition);
            }, 0);
          }, 0);
          break;
        }
      }
    }
  };

  // Helper function to find all bracket pairs in a string
  const findBracketPairs = (str: string): [number, number][] => {
    const pairs: [number, number][] = [];
    const stack: number[] = [];

    for (let i = 0; i < str.length; i++) {
      if (str[i] === '[') {
        stack.push(i);
      } else if (str[i] === ']' && stack.length > 0) {
        const start = stack.pop();
        pairs.push([start!, i]);
      }
    }

    return pairs;
  };

  // Helper function to find differences between two strings
  const findDifferences = (
    oldStr: string,
    newStr: string,
  ): [number, number] | null => {
    // Find the common prefix
    let i = 0;
    while (i < newStr.length && newStr[i] === oldStr[i]) {
      i++;
    }
    const startIndex = i;

    // Find the common suffix by working backwards
    let oldIndex = oldStr.length - 1;
    let newIndex = newStr.length - 1;

    while (
      newIndex >= 0 &&
      oldIndex >= 0 &&
      oldStr[oldIndex] === newStr[newIndex]
    ) {
      oldIndex--;
      newIndex--;
    }

    // Calculate the end of the deleted section
    const endIndex = oldStr.length - 1 - (newStr.length - 1 - newIndex);

    // If we found a difference
    if (startIndex <= endIndex) {
      return [startIndex, endIndex];
    }

    return null;
  };

  // Helper function to find additions between two strings
  const findAdditions = (
    oldStr: string,
    newStr: string,
  ): [number, string] | null => {
    if (oldStr.length >= newStr.length) return null;

    for (let i = 0; i < oldStr.length + 1; i++) {
      const oldPrefix = oldStr.substring(0, i);
      const oldSuffix = oldStr.substring(i);

      for (let j = i; j < newStr.length; j++) {
        const insertedText = newStr.substring(i, j + 1);

        if (newStr === oldPrefix + insertedText + oldSuffix) {
          return [i, insertedText];
        }
      }
    }
    return null;
  };

  const supportsInteractive = getValues('supportsInteractive');

  return (
    <StyledBox>
      <Stack direction="column" className={classes.container}>
        <SelectMenu
          name="messageType"
          label={t('workflows.type')}
          values={selectableMessageTypes}
          valueAttr="value"
          labelAttr="key"
          control={control}
          required
          errorMessage={formState.errors?.messageType?.message}
          rules={{ required: t('common.requiredField') }}
        />
        <SelectMenu
          name="recipient"
          label={t('workflows.sendTo')}
          values={selectableReceipients}
          valueAttr="value"
          labelAttr="key"
          control={control}
          required
          errorMessage={formState.errors?.recipient?.message}
          rules={{ required: t('common.requiredField') }}
        />
        <TextInput
          name="message"
          className={classes.messageInput}
          label={t('workflows.message')}
          control={control}
          errors={formState.errors}
          rules={{
            required: t('common.requiredField'),
            maxLength: {
              value: maxMessageLength,
              message: t('common.maxCharacters', {
                count: maxMessageLength,
              }),
            },
          }}
          inputProps={{
            minRows: 2,
            onClick: (event: React.MouseEvent<HTMLDivElement>) =>
              handleMessageInputClick(event, 'message'),
            onKeyUp: handleMessageInputKeyUp,
            onChange: handleMessageTextInputChange,
            ref: messageTextAreaRef,
          }}
          required
          multiline
        />

        <Stack
          direction="column"
          className={
            formState.errors.message
              ? classes.smartLabelMenuBarError
              : classes.smartLabelMenuBar
          }>
          <Divider className={classes.divider} />
          <Box className={classes.smartLabelMenuButtonContainer}>
            <IconButton
              aria-label="add-smart-label"
              onClick={(event) =>
                handleShowSmartLabelButtonClick(event, 'message')
              }>
              <AddIcon />
            </IconButton>
          </Box>
        </Stack>
        <Stack direction="column" className={classes.bottomContentContainer}>
          <Checkbox
            label={t('workflows.alertForSTOReservations')}
            name="alertForSTOReservations"
            className={classes.stoCheckbox}
            control={control}
          />
          {(isNil(supportsInteractive) || supportsInteractive) && (
            <>
              <SelectMenu
                name="interactiveResponse"
                label={t('workflows.interactiveResponse')}
                values={yesOrNoOptions}
                valueAttr="value"
                labelAttr="key"
                control={control}
              />
              {isInteractive && (
                <>
                  <TextInput
                    name="responseTimeoutMinutes"
                    label={t('workflows.timeout')}
                    type="number"
                    control={control}
                    inputProps={{
                      min: 1,
                      step: 1,
                    }}
                    required
                    rules={{
                      required: t('common.requiredField'),
                      min: {
                        value: 1,
                        message: t('workflows.rangeErrorGreaterThan', {
                          min: 0,
                        }),
                      },
                      validate: (value) => {
                        const num = Number(value);
                        if (!Number.isInteger(num)) {
                          return t('workflows.wholeNumberError');
                        }
                        return true;
                      },
                    }}
                    errors={formState.errors}
                  />
                  <SelectMenu
                    name="sendReminderIfNoResponse"
                    label={t('workflows.sendReminder')}
                    values={yesOrNoOptions}
                    valueAttr="value"
                    labelAttr="key"
                    control={control}
                  />
                  {sendReminder && (
                    <>
                      <TextInput
                        name="numberOfReminders"
                        label={t('workflows.numberOfReminders')}
                        type="number"
                        control={control}
                        inputProps={{
                          min: 1,
                          step: 1,
                        }}
                        required
                        rules={{
                          required: t('common.requiredField'),
                          min: {
                            value: 1,
                            message: t('workflows.rangeErrorGreaterThan', {
                              min: 0,
                            }),
                          },
                          validate: (value) => {
                            const num = Number(value);
                            if (!Number.isInteger(num)) {
                              return t('workflows.wholeNumberError');
                            }
                            return true;
                          },
                        }}
                        errors={formState.errors}
                      />
                      <TextInput
                        name="sendReminderAfterMinutes"
                        label={t('workflows.sendReminderAfter')}
                        type="number"
                        control={control}
                        inputProps={{
                          min: 1,
                          step: 1,
                        }}
                        required
                        rules={{
                          required: t('common.requiredField'),
                          min: {
                            value: 1,
                            message: t('workflows.rangeErrorGreaterThan', {
                              min: 0,
                            }),
                          },
                          validate: (value) => {
                            const num = Number(value);
                            if (!Number.isInteger(num)) {
                              return t('workflows.wholeNumberError');
                            }
                            return true;
                          },
                        }}
                        errors={formState.errors}
                      />
                      <TextInput
                        name="alertReminder"
                        className={classes.messageInput}
                        label={t('workflows.reminderMessage')}
                        readOnly={sameAsInitialMessage}
                        control={control}
                        errors={formState.errors}
                        rules={{
                          required: t('common.requiredField'),
                          maxLength: {
                            value: maxMessageLength,
                            message: t('common.maxCharacters', {
                              count: maxMessageLength,
                            }),
                          },
                        }}
                        inputProps={{
                          minRows: 2,
                          onClick: (event: React.MouseEvent<HTMLDivElement>) =>
                            handleMessageInputClick(event, 'alertReminder'),
                          onKeyUp: handleMessageInputKeyUp,
                          onChange: handleMessageTextInputChange,
                          ref: reminderMessageTextAreaRef,
                        }}
                        required
                        multiline
                      />
                      <Stack
                        direction="column"
                        className={
                          formState.errors.alertReminder
                            ? classes.smartLabelMenuBarError
                            : classes.smartLabelMenuBar
                        }>
                        <Divider className={classes.divider} />
                        <Box className={classes.smartLabelMenuButtonContainer}>
                          <IconButton
                            aria-label="add-smart-label"
                            disabled={sameAsInitialMessage}
                            onClick={(event) =>
                              handleShowSmartLabelButtonClick(
                                event,
                                'alertReminder',
                              )
                            }>
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Stack>
                      <Checkbox
                        name="sameAsInitialMessage"
                        label={t('workflows.sameAsInitialMessage')}
                        control={control}
                        className={classes.sameAsCheckbox}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Stack>

        <StyledMenu anchorEl={anchorEl} open={open}>
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Box className={classes.menu}>
              <TextSearch
                id="text-search"
                className={classes.search}
                icon={<Search />}
                label={t('workflows.search')}
                currentSearchFilter={smartLabelSearchText}
                onSearchChange={handleSearchTextChange}
              />
              {filteredSmartLabels.map((smartLabel) => (
                <MenuItem
                  onClick={() => {
                    handleSmartLabelClick(smartLabel);
                    setAnchorEl(null);
                  }}
                  className={classes.menuItem}>
                  <Typography variant="h5">
                    {smartLabel.smartLabelName}
                  </Typography>
                  <Typography variant="body1">
                    {smartLabel.description}
                  </Typography>
                </MenuItem>
              ))}
            </Box>
          </ClickAwayListener>
        </StyledMenu>
      </Stack>
    </StyledBox>
  );
}
